// Shared pieces of the conversation-quality pages: the verdict badges (always a word, never colour alone) and a
// ranked list of counts (one hue for magnitude, the value printed beside each bar).
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAsync } from '../../hooks/useAsync';
import { Badge, Select, type Tone } from '../../components/ui';
import type { Count, Outcome, Severity } from '../../types/api';

export const OUTCOMES: Outcome[] = ['success', 'partial', 'failure', 'abandoned', 'unclear'];
export const SEVERITIES: Severity[] = ['none', 'low', 'medium', 'high'];
export const CATEGORIES = ['build', 'modify', 'explain', 'audit', 'fix', 'format', 'other'];

export function outcomeTone(outcome: string | null): Tone {
  switch (outcome) {
    case 'success':
      return 'good';
    case 'partial':
      return 'warn';
    case 'failure':
    case 'abandoned':
      return 'bad';
    default:
      return 'neutral';
  }
}

export function severityTone(severity: string | null): Tone {
  return severity === 'high' ? 'bad' : severity === 'medium' ? 'warn' : 'neutral';
}

export function OutcomeBadge({ outcome }: { outcome: string | null }) {
  const { t } = useTranslation();
  if (!outcome) return <span className="text-fainter">—</span>;
  return <Badge tone={outcomeTone(outcome)}>{t(`quality.outcome.${outcome}`, outcome)}</Badge>;
}

export function SeverityBadge({ severity }: { severity: string | null }) {
  const { t } = useTranslation();
  if (!severity || severity === 'none') return null;
  return <Badge tone={severityTone(severity)}>{t(`quality.severity.${severity}`, severity)}</Badge>;
}

export function RatingBadge({ rating }: { rating: string | null }) {
  const { t } = useTranslation();
  if (!rating) return <span className="text-fainter">—</span>;
  return <Badge tone={rating === 'up' ? 'good' : 'bad'}>{t(`quality.rating.${rating}`)}</Badge>;
}

export function pct(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`;
}

/** A label for a slug of one of the vocabularies (`quality.<group>.<slug>`), the slug itself when unknown. */
export function useLabel(group: string) {
  const { t } = useTranslation();
  return (slug: string) => t(`quality.${group}.${slug}`, slug.replace(/_/g, ' '));
}

/** Counts as horizontal bars, largest first: one hue (magnitude), thin marks with a rounded end, the value as text. */
export function RankList({ items, label, empty, onPick }: { items: Count[]; label: (key: string) => string; empty: string; onPick?: (key: string) => void }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  if (!items.length) return <p className="text-[12.5px] text-faint">{empty}</p>;
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((item) => {
        const row = (
          <>
            <span className="w-40 shrink-0 truncate text-[12.5px] text-ink" title={label(item.key)}>{label(item.key)}</span>
            <span className="relative h-2.5 flex-1 rounded-r-[4px] bg-transparent">
              <span className="absolute inset-y-0 left-0 rounded-r-[4px] bg-accent" style={{ width: `${Math.max(2, (item.count / max) * 100)}%` }} />
            </span>
            <span className="w-10 shrink-0 text-right font-mono text-[11.5px] text-muted">{item.count}</span>
          </>
        );
        return (
          <li key={item.key} title={`${label(item.key)}: ${item.count}`}>
            {onPick ? (
              <button type="button" onClick={() => onPick(item.key)} className="flex w-full items-center gap-3 rounded-sm px-1 py-0.5 text-left hover:bg-hover-soft">{row}</button>
            ) : (
              <div className="flex items-center gap-3 px-1 py-0.5">{row}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The organizations as a plain select (the filter keeps only the id in the URL). */
export function OrgSelect({ value, onChange }: { value: number | undefined; onChange: (id: number | undefined) => void }) {
  const { t } = useTranslation();
  const { data } = useAsync(() => api.organizations({ page_size: 200 }), []);
  return (
    <Select value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)} aria-label={t('quality.organization')}>
      <option value="">{t('common.any')}</option>
      {(data?.items ?? []).map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </Select>
  );
}
